import { FULFILLABLE_STATUSES } from "./item";
import type { SearchCatalogItemsFilter } from "./search";

/**
 * What an inventory count is of. Single covers a raw card (`SINGLE` / `SINGLES`, or a type that
 * was never set) and leaves out a boxed custom item that still has no kind. Slabbed, sealed and
 * other are the kind stored on the item. Unclassified is a boxed custom item whose kind was
 * never set, so a count can still find it.
 */
export const INVENTORY_KINDS = ["SINGLE", "SLABBED", "SEALED", "OTHER", "UNCLASSIFIED"] as const;
export type InventoryKind = (typeof INVENTORY_KINDS)[number];

export interface InventoryCatalogQuery {
  query?: string;
  /** Absent counts every kind. */
  kind?: InventoryKind;
  /** A card-game or sport tag, the Retool category select. Absent counts every category. */
  tag?: string;
  /** Inclusive cents bounds. A count asks for a price, so a row with no value stays out. */
  valueMin: number;
  valueMax: number;
  /** Inclusive. Absent counts every quantity. */
  quantityMin?: number;
  /** Archived rows stay out until a count asks for them. Risky grades stay in either way. */
  showArchived: boolean;
}

/** The catalog search a count runs. Name order, one page at a time. */
export function inventoryCatalogFilter(input: InventoryCatalogQuery): SearchCatalogItemsFilter {
  const filter: SearchCatalogItemsFilter = {
    query: input.query,
    tags: input.tag ? [input.tag] : undefined,
    valueMin: input.valueMin,
    valueMax: input.valueMax,
    quantityMin: input.quantityMin,
    sort: "name",
    includePreRelease: true,
    includeArchived: input.showArchived,
    // The browse default also hides risky grades. A count is of what is on the shelf.
    fulfillableStatusExclude: input.showArchived ? undefined : ["ARCHIVED"],
    fulfillableStatus: input.showArchived ? [...FULFILLABLE_STATUSES] : undefined,
  };
  if (input.kind === "SINGLE") {
    filter.types = ["SINGLE", "SINGLES"];
    filter.includeUnsetType = true;
    filter.excludeBoxedProtocol = "CUSTOM_TCG";
  } else if (input.kind === "UNCLASSIFIED") {
    filter.includeUnsetType = true;
    filter.boxedProtocol = "CUSTOM_TCG";
  } else if (input.kind) {
    filter.types = [input.kind];
  }
  return filter;
}
